import { gql } from '@apollo/client';
import { Box, Checkbox, Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import { t } from 'i18next';
import {
  //  useContext, useEffect,
  useMemo,
} from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  Column,
  Row,
  TableInstance,
  useFilters,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';
import { Columns } from '~components/displays/DisplayTable/constants';
import { useDisplaysQuery } from '~components/displays/DisplayTable/useDisplaysQuery';
import { ChevronDownIcon, ChevronUpIcon } from '~components/ui/icons';
import { SelectOption } from '~components/ui/Select';
import { StopClickPropagation } from '~components/ui/StopClickPropagation';
import { DataWithCustomer } from '~pages/[organization]/customers/[handle]/displays/Index';
import { useAnalyticsReporter } from '~utils/analytics';
import { compareMaybeStrings } from '~utils/compare';
import { useFeatureFlag } from '~utils/features';
import { MaybePromise } from '~utils/types';
import { useDisplayPresence } from '../useDisplayPresence';
import { useStatus } from '../useStatus';
import { DisplayTable_DisplayFragment } from './__generated__/DisplayTableServerSide.graphql';
import { AliasCell } from './Cells/AliasCell';
import { ConfigurationCell } from './Cells/ConfigurationCell';
import { ContentSourceCell, extractSource } from './Cells/ContentSourceCell';
import { FirmwareCell } from './Cells/FirmwareCell';
import { GroupsCell } from './Cells/GroupsCell';
import { PlaylistCell } from './Cells/PlaylistCell';
import { PowerScheduleCell } from './Cells/PowerScheduleCell';
import { ScheduleCell } from './Cells/ScheduleCell';
import { SiteCell } from './Cells/SiteCell';
import { StatusCell } from './Cells/StatusCell';
import { VolumeCell } from './Cells/VolumeCell';
import { WarningsCell } from './Cells/WarningsCell';
import {
  filterByFirmwareServerSide,
  filterByGroupsServerSide,
  filterByPlaylistServerSide,
  filterByPowerScheduleServerSide,
  filterBySiteServerSide,
  filterRowByStatusServerSide,
  filterRowByWarningStatusServerSide,
  FilterServerSide,
} from './Filtering/FilterServerSide';
import { useFilterOptionsServerSide } from './Filtering/useFilterOptionsServerSide';
import {
  DisplayTablePageSizeSelectorServerSide,
  DisplayTablePaginationServerSide,
} from './PaginationServerSide';
import { SearchBarServerSide } from './SearchBarServerSide';

export function useDisplayTableServerSide({
  data,
  pageIndex,
  pageSize,
  totalRowCount,
  hasWaveSubscription,
}: {
  data: DataWithCustomer;
  pageIndex: number;
  pageSize: number;
  totalRowCount: number;
  hasWaveSubscription?: boolean;
}) {
  const { configurationId } = useParams();
  const { pathname } = useLocation();
  const isInConfigurationSummary = Boolean(configurationId) && pathname.endsWith('/config-devices');
  const { sortByPresenceAndPower } = useDisplayPresence();
  const { getStatus, sortByWarningSeverity } = useStatus();
  const { getPresence, getPowerState } = useDisplayPresence();
  const { isEnabled: isSchedulesEnabled } = useFeatureFlag('schedules');
  const columns = useMemo<Array<Column<DisplayTable_DisplayFragment>>>(
    () => [
      {
        id: Columns.Selection,
        Header: ({ getToggleAllPageRowsSelectedProps }) => {
          const { checked, indeterminate, onChange, role, title } =
            getToggleAllPageRowsSelectedProps();

          if (isInConfigurationSummary) return null;
          return (
            <Checkbox
              isChecked={checked}
              isIndeterminate={indeterminate}
              onChange={onChange}
              role={role}
              title={title}
            />
          );
        },
        Cell: ({ row }: { row: Row<DisplayTable_DisplayFragment> }) => {
          const { checked, indeterminate, onChange, role, title } = row.getToggleRowSelectedProps();

          if (isInConfigurationSummary) return null;
          return (
            <StopClickPropagation>
              <Checkbox
                isChecked={checked}
                isIndeterminate={indeterminate}
                onChange={onChange}
                role={role}
                title={title}
              />
            </StopClickPropagation>
          );
        },
        width: 'auto',
      },
      {
        id: Columns.Status,
        Header: t('status'),
        accessor: (d) => d,
        sortType: (rowA, rowB) => sortByPresenceAndPower(rowA.original, rowB.original),
        filter: (
          rows: Array<Row<DisplayTable_DisplayFragment>>,
          _: string[],
          filterValues: SelectOption[],
        ) =>
          rows.filter((row) => {
            const presence = getPresence(row.original);
            const powerState = getPowerState(row.original);

            return filterRowByStatusServerSide(row, presence, powerState, filterValues);
          }),
        Cell: StatusCell,
        width: 'auto',
      },
      {
        id: Columns.Alias,
        Header: t('alias'),
        accessor: (d) => d.alias,
        sortType: (rowA, rowB) => compareMaybeStrings(rowA.original.alias, rowB.original.alias),
        Cell: AliasCell,
        width: 'auto',
        maxWidth: 180,
      },
      {
        id: Columns.Site,
        accessor: (d) => d.site,
        Header: t('site'),
        sortType: (rowA, rowB) =>
          compareMaybeStrings(rowA.original.site?.name, rowB.original.site?.name),
        filter: filterBySiteServerSide,
        Cell: SiteCell,
        width: '180px',
        minWidth: 180,
        maxWidth: 180,
      },
      {
        // Hidden
        id: Columns.Firmware,
        Header: t('firmware'),
        accessor: (d) => d.firmware,
        sortType: (rowA, rowB) =>
          compareMaybeStrings(
            rowA.original.firmware?.android.version,
            rowB.original.firmware?.android.version,
          ),
        filter: filterByFirmwareServerSide,
        Cell: FirmwareCell,
        width: 'auto',
        minWidth: 100,
        maxWidth: 100,
      },
      {
        // Hidden
        id: Columns.Playlist,
        Header: t('playlist'),
        accessor: (d) => d.playlist,
        sortType: (rowA, rowB) =>
          compareMaybeStrings(
            rowA.original.playlist?.current?.title,
            rowB.original.playlist?.current?.title,
          ),
        filter: filterByPlaylistServerSide,
        Cell: PlaylistCell,
        width: 'auto',
        minWidth: 100,
        maxWidth: 100,
      },
      {
        // Hidden
        id: Columns.PowerSchedule,
        Header: isSchedulesEnabled ? t('schedule') : t('powerSchedule'),
        accessor: (d) => (isSchedulesEnabled ? d.schedule : d.powerSchedule),
        sortType: (rowA, rowB) =>
          isSchedulesEnabled
            ? compareMaybeStrings(
                rowA.original.schedule?.schedule?.title,
                rowB.original.schedule?.schedule?.title,
              )
            : compareMaybeStrings(
                rowA.original.powerSchedule?.schedule?.title,
                rowB.original.powerSchedule?.schedule?.title,
              ),
        filter: filterByPowerScheduleServerSide,
        Cell: isSchedulesEnabled ? ScheduleCell : PowerScheduleCell,
        width: 'auto',
        minWidth: 100,
        maxWidth: 100,
      },
      {
        id: Columns.Groups,
        Header: t('groups'),
        accessor: (d) => d.groups,
        disableSortBy: true,
        filter: filterByGroupsServerSide,
        Cell: GroupsCell,
        width: '120px',
        maxWidth: 120,
        minWidth: 120,
      },
      {
        id: Columns.Warnings,
        Header: t('warnings'),
        accessor: (d) => d,
        sortType: (rowA, rowB) => sortByWarningSeverity(rowA.original, rowB.original),
        // Since we need to filter on `status`, and getting that status relies on hooks, mapping the rows is lifted into
        // this file, and the filter itself handles only single rows
        filter: (
          rows: Array<Row<DisplayTable_DisplayFragment>>,
          _: string[],
          filterValues: SelectOption[],
        ) =>
          rows.filter((row) => {
            const status = getStatus(row.original);
            return filterRowByWarningStatusServerSide(row, status, filterValues);
          }),
        Cell: WarningsCell,
        width: '100%',
        minWidth: 100, // yep, no toucherino.
      },
      {
        id: Columns.ContentSource,
        Header: t('activeSource'),
        accessor: (d) => d.contentSource,
        sortType: (rowA, rowB) =>
          compareMaybeStrings(
            extractSource(rowA.original.contentSource?.current?.desired) ??
              extractSource(rowA.original.contentSource?.current?.reported),
            extractSource(rowB.original.contentSource?.current?.desired) ??
              extractSource(rowB.original.contentSource?.current?.reported),
          ),
        Cell: ContentSourceCell,
        width: '150px',
        maxWidth: 150,
        minWidth: 150,
      },
      {
        id: Columns.Configuration,
        Header: t('configuration'),
        accessor: (d) => d,
        disableSortBy: true,
        Cell: ConfigurationCell,
        width: 'auto',
        maxWidth: 320,
      },
      {
        id: Columns.Volume,
        Header: t('volume'),
        accessor: (d) => d.volume,
        disableSortBy: true,
        Cell: VolumeCell,
        width: 'auto',
        minWidth: 90,
        maxWidth: 90,
      },
    ],
    [
      isInConfigurationSummary,
      sortByPresenceAndPower,
      getPresence,
      getPowerState,
      sortByWarningSeverity,
      getStatus,
      isSchedulesEnabled,
    ],
  );

  const filterOptions = useFilterOptionsServerSide([], data);
  const { persistedDisplaysState } = useDisplaysQuery(hasWaveSubscription);

  return useTable(
    {
      columns,
      data: data.customer?.displays || [],
      initialState: {
        pageIndex,
        pageSize,
        hiddenColumns: [
          Columns.Firmware,
          Columns.Playlist,
          Columns.PowerSchedule,
          ...(hasWaveSubscription ? [] : [Columns.Configuration]),
        ],
        ...persistedDisplaysState?.(filterOptions),
      },
      autoResetSortBy: false,
      autoResetFilters: false,
      autoResetSelectedRows: false,
      autoResetPage: false,
      manualPagination: true,
      pageCount: Math.ceil(totalRowCount / pageSize),
    },
    useFilters,
    useSortBy,
    usePagination,
    useRowSelect,
  );
}

/* eslint-disable react/jsx-key */
export function DisplayTableServerSide({
  table,
  onGoToDetail,
  invalidDisplayIds,
}: {
  table: TableInstance<DisplayTable_DisplayFragment>;
  onGoToDetail: (id: string) => MaybePromise<void>;
  invalidDisplayIds?: string[];
}) {
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page } = table;
  const analytics = useAnalyticsReporter();

  return (
    <Table {...getTableProps()} variant="simple">
      <Thead>
        {headerGroups.map((headerGroup) => (
          <Tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column) => {
              const headerProps = column.getHeaderProps(column.getSortByToggleProps());
              return (
                <Th
                  {...headerProps}
                  color={column.isSorted ? 'gray.700' : 'gray.600'}
                  minWidth={`${column.minWidth}px`}
                  maxWidth={`${column.maxWidth}px`}
                  width={column.width}
                  _hover={
                    column.disableSortBy
                      ? {}
                      : {
                          textColor: 'gray.800',
                        }
                  }
                  onClick={(e) => {
                    if (column.id !== 'selection') {
                      analytics.track('displaySort', { sortType: column.id });
                    }
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (headerProps as any).onClick?.(e);
                  }}
                >
                  <Box display="flex" flexDirection="row" alignItems="center">
                    <Box flex="1">{column.render('Header')}</Box>
                    {column.canSort && (
                      <Box>
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <ChevronDownIcon display="inherit" />
                          ) : (
                            <ChevronUpIcon display="inherit" />
                          )
                        ) : (
                          <ChevronDownIcon visibility="hidden" />
                        )}
                      </Box>
                    )}
                  </Box>
                </Th>
              );
            })}
          </Tr>
        ))}
      </Thead>
      <Tbody {...getTableBodyProps()}>
        {page.map((row) => {
          prepareRow(row);

          const hasServerErrors = invalidDisplayIds?.includes(row.original.id);
          const hasEmptyShadow = row.original.hasEmptyShadow;

          return (
            <Tr
              {...row.getRowProps()}
              {...(hasServerErrors
                ? {
                    background: 'gray.50',
                  }
                : {
                    role: 'button',
                    background: hasEmptyShadow ? 'red.25' : 'inherit',
                    _hover: {
                      background: hasEmptyShadow ? 'red.50' : 'blue.50',
                      cursor: 'pointer',
                    },
                  })}
            >
              {row.cells.map((cell) => {
                return (
                  <Td
                    minWidth={`${cell.column.minWidth}px`}
                    maxWidth={`${cell.column.maxWidth}px`}
                    width={cell.column.width}
                    onClick={
                      hasServerErrors
                        ? undefined
                        : cell.column.id === Columns.Selection
                        ? () => cell.row.toggleRowSelected()
                        : (e) => {
                            if (cell.column.id === Columns.Configuration) {
                              e.stopPropagation();
                            } else {
                              analytics.track('displayDetail');
                              onGoToDetail(row.original.id);
                            }
                          }
                    }
                    {...(hasServerErrors ? { opacity: 0.5 } : {})}
                    {...cell.getCellProps()}
                  >
                    {cell.column.id === Columns.Selection && hasServerErrors
                      ? null
                      : cell.render('Cell')}
                  </Td>
                );
              })}
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );
}
/* eslint-enable react/jsx-key */

DisplayTableServerSide.Filter = FilterServerSide;
DisplayTableServerSide.Pagination = DisplayTablePaginationServerSide;
DisplayTableServerSide.PageSizeSelector = DisplayTablePageSizeSelectorServerSide;
DisplayTableServerSide.SearchSelector = SearchBarServerSide;

DisplayTableServerSide.graphql = {
  fragments: {
    DisplayTable_display: gql`
      fragment DisplayTable_display on Display {
        id
        commercialTypeNumber
        networkInformation {
          ethernetMacAddress
          localIPAddress
          wifiMacAddress
        }
        hasEmptyShadow
        ...UseDisplayPresence_display
        ...UseRecommendedSettings_display
        ...AliasCell_display
        ...CTNCell_display
        ...GroupsCell_display
        ...ContentSourceCell_display
        ...SiteCell_display
        ...StatusCell_display
        ...FirmwareCell_display
        ...PlaylistCell_display
        ...PowerScheduleCell_display
        ...ScheduleCell_display
        ...VolumeCell_display
        ...BookmarksCell_display
        ...WarningsCell_display
        ...PlatformCell_display
        ...ConfigurationCell_display
        ...PlaylistTypeCell_display
      }
    `,
  },
};
